import React, {useState, useMemo, useEffect} from 'react'
import {Alert} from 'reactstrap'
import {useTranslation} from 'react-i18next'
import compose from 'lodash/flowRight'

import useSmartNavigate from 'hooks/useSmartNavigate'
import withGeneratorDataSource from 'hocs/withGeneratorDataSource'
import withInitialDataLoadWaiting from 'hocs/withInitialDataLoadWaiting'
import SaveButtonsBlock from 'components/blocks/SaveButtonsBlock'
import GeneratorBlock from 'components/fieldBlocks/GeneratorBlock'
import {queryLicenseEvents, queryStudentsWithUsers} from '../data/adminQueries'
import {createExamTimeCorrection} from '../data/adminMutations'
import {breadcrumbsStore} from 'appBase/TopNav'
import {parseBackendMessage} from 'utils/translationUtils'

const ExamTimeCorrectionsView = compose(
    queryLicenseEvents,
    queryStudentsWithUsers,
    createExamTimeCorrection,
    withGeneratorDataSource,
    withInitialDataLoadWaiting(['licenseEvents', 'studentsWithUsers'])
)((props) => {
    const {licenseEvents, studentsWithUsers, dataSource, setDataSource,
           onChange,  onValidate, isChanged, hasErrors, saveAndReset, createExamTimeCorrection} = props;

    const navigate = useSmartNavigate();
    const {t} = useTranslation();
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    // links
    const containerId = 'examTimeCorrections';
    breadcrumbsStore.register(containerId);

    const links = [{
        name: t('appAdmin.views.ExamTimeCorrectionsView.timeCorrections'),
        to: `${props.uri}${props.location.search}`
    }];

    useEffect(() => {
        const isTerminalBreadcrumbs = true;
        breadcrumbsStore.set(containerId, links, isTerminalBreadcrumbs);
    });

    // dataSource
    const examTimeCorrectionData = useMemo(() => ({}), []);
    useEffect(() => {
        setDataSource(
            examTimeCorrectionData,
            (data) => {
                createExamTimeCorrection(data)
                    .then(({data: {createExamTimeCorrection: {result, error}}}) => {
                        setResult(result);
                        setError(error ? parseBackendMessage(error, t): t('appAdmin.views.ExamTimeCorrectionsView.success'));
                        navigate(`${props.uri}${props.location.search}`);
                    });
            });
    }, [examTimeCorrectionData, dataSource, setDataSource, t, createExamTimeCorrection,
        error, props.uri, props.location.search, navigate]);

    // events (exams) and users
    const events = licenseEvents.filter(e => e.isCompetitionMode)
                                .map(e => ({name: e.name, value: e.id}));
    const users = studentsWithUsers.filter(({student, user}) => user)
                                   .map(({student, user}) =>
                                        ({name: `${user.name} ${student.email}`, value: user.id}));
    //onCancel
    const onCancelEdit = () => {
        setDataSource(null);
        setError("");
        setResult(null);
        navigate(`${props.uri}${props.location.search}`);
    };

    return (
            <div className="scroll flex-grow d-flex flex-column">
                <h5 className="text-center mt-3">{t('appAdmin.views.ExamTimeCorrectionsView.timeCorrections')}</h5>
                {error &&
                    <Alert color={result ? "success": "danger"}>{error}</Alert>}

                <SaveButtonsBlock isChanged={isChanged} hasErrors={hasErrors} onSave={saveAndReset} onCancel={onCancelEdit} />
                <GeneratorBlock data={dataSource} onChange={onChange} onValidate={onValidate} wide
                        items={[{
                            type: "select",
                            nullOption: t('appAdmin.views.ExamTimeCorrectionsView.eventNotSelected'),
                            name: t('appAdmin.views.ExamTimeCorrectionsView.events'),
                            field: "eventId",
                            required: true,
                            options: events
                        },
                        {
                            type: "select",
                            nullOption: t('appAdmin.views.ExamTimeCorrectionsView.userNotSelected'),
                            name: t('appAdmin.views.ExamTimeCorrectionsView.users'),
                            field: "userId",
                            required: true,
                            options: users
                        },
                        {
                            type: "int",
                            name: t('appAdmin.views.ExamTimeCorrectionsView.additionalTime'),
                            field: "additionalTime",
                            required: true,
                        }]} />
             </div>
        );
});

export default ExamTimeCorrectionsView;